import {
  borrowerTypeDropDownValues,
  bridgeEntityTypeDropDownValues,
  entityTypeDropDownValues,
  thirtyYearCreditEvent
} from "../../constants";
import {
  foreignNationalOptions,
  yesNoDropdown
} from "../../../create-loan/constants/loanCreationDropDownValues";
import { LoanSizerEnum } from "../../../frontend-sizer/FrontendSizerTemplate";
import { bridgeLoanFields } from "../../../create-loan/constants/loanFieldType";
import { CreditEventDropDown } from "../../../create-loan/form-components/LoanInformation";

const isRequired = (
  fieldName: any,
  borrowerInfo: any,
  borrowersData: any,
  loanStage: any
) => {
  const hasIndividualBorrower = borrowersData?.find(
    (ele: any) => ele?.payload?.isPrimaryBorrower
  );
  const firstEntityBorrowerId = borrowersData?.find(
    (ele: any) => ele.payload?.borrowerType === "Entity"
  )?.borrowerId;

  let required =
    (borrowerInfo?.isPrimaryBorrower &&
      !bridgeLoanFields[fieldName]?.optionalIn?.includes(loanStage)) ||
    (!hasIndividualBorrower &&
      !bridgeLoanFields[fieldName]?.optionalIn?.includes(loanStage));

  if (required && fieldName === "originalCreditScoreMedian") {
    if (borrowerInfo?.foreignNationalString === "Yes") {
      required = false;
    }
  }
  return required;
};

const borrowerType = {
  fieldName: "borrowerType",
  fieldLabel: "Borrower Type",
  backendKey: "loanUserType",
  required: () => true,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: borrowerTypeDropDownValues,
  colSpan: 4,
  isVisible: () => true,
  commentField: "loanUserType",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const isPrimaryBorrower = {
  fieldName: "isPrimaryBorrower",
  fieldLabel: "Primary Borrower",
  backendKey: "isPrimary",
  required: () => true,
  fieldType: () => "radioGroup",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Individual",
  commentField: "borrowerType",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const firstName = {
  fieldName: "firstName",
  backendKey: "firstName",
  fieldLabel: "First Name",
  required: () => true,
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Individual",
  commentField: "customer.firstName",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastName = {
  fieldName: "lastName",
  fieldLabel: "Last Name",
  backendKey: "lastName",
  required: () => true,
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Individual",
  commentField: "customer.lastName",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const einNumber = {
  fieldType: () => "string",
  fieldName: "eiNumber",
  fieldLabel: "EIN",
  required: () => true,
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Entity",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const ssNumber = {
  fieldType: () => "string",
  fieldName: "ssNumber",
  fieldLabel: "SSN",
  required: () => true,
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Entity",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const foreignNational = {
  fieldName: "foreignNationalString",
  fieldLabel: "Foreign National",
  backendKey: "foreignNationalString",
  required: () => true,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: yesNoDropdown.map((item) => ({ value: item, label: item })),
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Individual",
  commentField: "customer.foreignNationalString",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const originalCreditScoreMedian = {
  fieldName: "originalCreditScoreMedian",
  backendKey: "originalCreditScoreMedian",
  fieldLabel: "Original Credit Score (Median)",
  required: (borrowerInfo: any) => {
    if (
      borrowerInfo?.foreignNationalString &&
      borrowerInfo?.foreignNationalString !== "Yes"
    ) {
      return true;
    }
    return false;
  },
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType !== "Entity",
  commentField: "originalCreditScoreMedian",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const originalCreditReportDate = {
  fieldName: "originalCreditReportDate",
  backendKey: "originalCreditScoreReportDate",
  fieldLabel: "Original Credit Report Date",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) =>
    isRequired(
      "originalCreditReportDate",
      borrowerInfo,
      borrowersData,
      loanStage
    ),
  fieldType: () => "date",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType !== "Entity",
  commentField: "originalCreditScoreReportDate",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const borrowingEntityName = {
  fieldName: "borrowingEntityName",
  fieldLabel: "Borrower Entity Name",
  backendKey: "accountName",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) =>
    isRequired("borrowingEntityName", borrowerInfo, borrowersData, loanStage),
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Entity",
  commentField: "customer.accountName",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrBorrowingEntityName = {
  fieldName: "borrowingEntityName",
  fieldLabel: "Borrower Entity Name",
  backendKey: "accountName",
  required: () => true,
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Entity",
  commentField: "customer.accountName",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const borrowerEmail = {
  fieldName: "borrowerEmail",
  fieldLabel: "Borrower Email",
  backendKey: "email",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => borrowerInfo?.fundingType === "table_funding",
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: `customer.contactList[0].email`,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const liquidityCurrency = {
  fieldName: "liquidityCurrency",
  fieldLabel: "Liquidity Currency",
  required: () => false,
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const billingAddress = {
  fieldName: "billingAddress",
  fieldLabel: "Borrower Full Billing Address",
  backendKey: "addressLine1",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: (loanType: string) => "autocomplete",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: `customer.addressList[0].addressLine1`,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const billingPhoneNumber = {
  fieldName: "billingPhoneNumber",
  fieldLabel: "Borrower Billing Phone Number",
  backendKey: "contactNumber",
  required: () => false,
  fieldType: (loanType: string) => "phone",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: `customer.contactList[0].contactNumber`,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const borrowerExperience = {
  fieldName: "borrowerExperience",
  fieldLabel: "Experience in the last 3 years",
  backendKey: "experience",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) =>
    isRequired("borrowerExperience", borrowerInfo, borrowersData, loanStage),
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: (data: any) => true,
  getTypoErrorMsg: () => "Please enter a valid number",
  commentField: "experience",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const experienceIn3To10Years = {
  fieldName: "experienceIn3To10Years",
  fieldLabel: "Experience in the last 3 to 10 years",
  backendKey: "experienceIn3To10Years",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: (data: any) => true,
  getTypoErrorMsg: () => "Please enter a valid number",
  commentField: "experienceIn3To10Years",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const borrowerGUCExperience = {
  fieldName: "borrowerGUCExperience",
  backendKey: "borrowerGUCExperience",
  fieldLabel: "Borrower GUC Experience",
  required: () => true,
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: () => true,
  commentField: "borrowerGUCExperience",
  getTypoErrorMsg: () => "Please enter a valid number",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const heavyRehabExperience = {
  fieldName: "heavyRehabExperience",
  backendKey: "heavyRehabExperience",
  fieldLabel: "Fix/Flip Experience",
  getTypoErrorMsg: () => "Please enter a valid number",
  required: () => true,
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: () => true,
  commentField: "heavyRehabExperience",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const borrowerCreditScore = {
  fieldName: "borrowerCreditScore",
  backendKey: "originalCreditScoreMedian",
  fieldLabel: "Credit score- Actual",
  fieldId: "fes-CreditscoreActual",
  required: (borrowerInfo: any) => {
    if (
      borrowerInfo?.foreignNationalString &&
      borrowerInfo?.foreignNationalString !== "Yes"
    ) {
      return true;
    }
    return false;
  },
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "originalCreditScoreMedian",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const bridgeForeignNationString = {
  fieldName: "foreignNationalString",
  fieldLabel: "Foreign National Flag(Borrower or Guarantor)",
  fieldId: "fes-foreignNationalFlag",
  ruleFieldName: "foreignNationalString",
  fieldType: () => "dropDown",
  extraData: foreignNationalOptions.map((item: any) => ({
    value: item,
    label: item
  })),
  isVisible: () => true,
  commentField: "customer.foreignNationalString",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrForeignNationString = {
  fieldName: "foreignNationalString",
  fieldLabel: "Foreign National Flag",
  backendKey: "foreignNationalString",
  required: () => true,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: foreignNationalOptions.map((item) => ({
    value: item,
    label: item
  })),
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType !== "Entity",
  commentField: "customer.foreignNationalString",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrFirstTimeHomeBuyer = {
  fieldName: "firstTimeHomeBuyer",
  fieldLabel: "First Time Home Buyer",
  backendKey: "firstTimeHomeBuyer",
  required: () => true,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: yesNoDropdown.map((item) => ({ value: item, label: item })),
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType !== "Entity",
  commentField: "firstTimeHomeBuyer",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const inState = {
  fieldName: "inState",
  fieldLabel: "In State",
  backendKey: "inState",
  required: () => false,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: yesNoDropdown.map((item: any) => ({
    value: item === "Yes" ? "Y" : "N",
    label: item
  })),
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "inState",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const creditEvent = {
  fieldName: "creditEvent",
  backendKey: "creditEvent",
  editedLabelPath: "creditEvent",
  fieldLabel: "FC/SS/DIL/BK",
  required: (data: any) => data?.borrowerType === "Individual",
  fieldType: (loanType: string) => "booleanDropDown",
  dropDownData: thirtyYearCreditEvent || [],
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType === "Individual",
  fieldComponent: CreditEventDropDown,
  errorMsg: "Please select value",
  commentField: "customer.creditEvent",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastFcDate = {
  fieldName: "lastFcDate",
  backendKey: "lastFcDate",
  fieldLabel: "Last Foreclosure Date",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "date",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastFcDate",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastSsDate = {
  fieldName: "lastSsDate",
  backendKey: "lastSsDate",
  fieldLabel: "Last Short Sale Date",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "date",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastSsDate",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastDilDate = {
  fieldName: "lastDilDate",
  backendKey: "lastDilDate",
  fieldLabel: "Last Deeds in Lieu Date",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "date",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastDilDate",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastBkDate = {
  fieldName: "lastBkDate",
  backendKey: "lastBkDate",
  fieldLabel: "Last Bankruptcy Date",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "date",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastBkDate",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastDelinquencyDate = {
  fieldName: "lastDelinquencyDate",
  backendKey: "lastDelinquencyDate",
  fieldLabel: "Last Delinquency Date",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "date",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastDelinquencyDate",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const lastSsMonths = {
  fieldName: "lastSsMonths",
  backendKey: "lastSsMonths",
  fieldLabel: "Months Since Short Sale",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "string",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastSsMonths",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true
};

const lastDilMonths = {
  fieldName: "lastDilMonths",
  backendKey: "lastDilMonths",
  fieldLabel: "Months Since Deeds in Lieu",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "string",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastDilMonths",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true
};

const lastBkMonths = {
  fieldName: "lastBkMonths",
  backendKey: "lastBkMonths",
  fieldLabel: "Months Since Bankruptcy",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "string",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastBkMonths",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true
};

const lastFcMonths = {
  fieldName: "lastFcMonths",
  backendKey: "lastFcMonths",
  fieldLabel: "Months Since Foreclosure",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "string",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastFcMonths",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true
};

const lastDelinquencyMonths = {
  fieldName: "lastDelinquencyMonths",
  backendKey: "lastDelinquencyMonths",
  fieldLabel: "Months Since Delinquency",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: () => "string",
  colSpan: 3,
  isVisible: (data: any) => data?.creditEvent === "Yes",
  commentField: "customer.lastDelinquencyMonths",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true
};

const fesBorrowerFields = [
  { ...borrowerCreditScore },
  {
    ...dscrForeignNationString,
    isVisible: () => true,
    fieldLabel: "Foreign National Flag(Borrower or Guarantor)",
    required: () => true
  },
  {
    ...borrowerType,
    required: () => false,
    isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
      isEvaluationPanelVisible
  },
  dscrFirstTimeHomeBuyer
];

const BridgeFes14Fields = [
  borrowerExperience,
  experienceIn3To10Years,
  {
    ...foreignNational,
    required: () => false,
    dropDownData: yesNoDropdown.map((item: any) => ({
      value: item,
      label: item
    }))
  },
  {
    ...borrowerCreditScore,
    fieldLabel: "Original Credit Score (Median)"
  }
];

const BridgeFesMultifamilyFields = [
  borrowerExperience,
  experienceIn3To10Years,
  {
    ...foreignNational,
    required: () => false,
    dropDownData: yesNoDropdown.map((item: any) => ({
      value: item,
      label: item
    }))
  },
  {
    ...borrowerCreditScore,
    required: () => false,
    fieldLabel: "Original Credit Score (Median)"
  }
];

const DSCRPrePostFields = [
  borrowerType,
  dscrBorrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  dscrForeignNationString,
  originalCreditScoreMedian,
  originalCreditReportDate,
  borrowerEmail,
  billingAddress,
  { ...billingPhoneNumber, required: () => false },
  dscrFirstTimeHomeBuyer,
  creditEvent
  // borrowerExperience
];

const DSCRPostFields = [
  borrowerType,
  dscrBorrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  dscrForeignNationString,
  originalCreditScoreMedian,
  originalCreditReportDate,
  { ...borrowerEmail, required: () => true },
  { ...billingAddress, required: () => false },
  billingPhoneNumber,
  dscrFirstTimeHomeBuyer,
  creditEvent
  // borrowerExperience
];

const bridgePreFields = [
  borrowerType,
  borrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  foreignNational,
  originalCreditScoreMedian,
  originalCreditReportDate,
  { ...borrowerEmail },
  billingAddress,
  { ...billingPhoneNumber, required: () => false }
];

const bridgePostFields = [
  borrowerType,
  borrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  foreignNational,
  originalCreditScoreMedian,
  originalCreditReportDate,
  { ...borrowerEmail, required: () => true },
  { ...billingAddress, required: () => false },
  billingPhoneNumber
];

const getBridgeFESFields = (toorakProduct = null) => {
  if (toorakProduct === LoanSizerEnum.oneFourFamily) {
    return [...BridgeFes14Fields, inState];
  }
  if (toorakProduct === LoanSizerEnum.multifamilyMixedUse) {
    return BridgeFesMultifamilyFields;
  }
  return [
    borrowerGUCExperience,
    heavyRehabExperience,
    {
      ...foreignNational,
      required: () => false,
      dropDownData: yesNoDropdown.map((item: any) => ({
        value: item,
        label: item
      }))
    },
    { ...borrowerCreditScore, fieldLabel: "Borrower Credit Score (Median)" }
  ];
};

const getBridgePreFields = (toorakProduct = null, loanStage = "") => {
  if (toorakProduct === LoanSizerEnum.groundUp) {
    return [
      ...bridgePreFields,
      borrowerGUCExperience,
      heavyRehabExperience,
      creditEvent
    ];
  }
  return [
    ...bridgePreFields,
    borrowerExperience,
    experienceIn3To10Years,
    creditEvent
  ];
};

const getBridgePostFields = (toorakProduct = null, loanStage = "") => {
  if (toorakProduct === LoanSizerEnum.groundUp) {
    return [
      ...bridgePostFields,
      borrowerGUCExperience,
      heavyRehabExperience,
      creditEvent
    ];
  }
  return [
    ...bridgePostFields,
    borrowerExperience,
    experienceIn3To10Years,
    creditEvent
  ];
};

export const getBorrowerInformationFields = (
  loanStage: string,
  loanType?: string,
  toorakProduct?: any
) => {
  switch (loanStage) {
    case "fes":
      return loanType === "InvestorDSCR"
        ? fesBorrowerFields
        : getBridgeFESFields(toorakProduct);
    case "pre":
      return loanType === "InvestorDSCR"
        ? DSCRPrePostFields
        : getBridgePreFields(toorakProduct, loanStage);
    case "post":
      return loanType === "InvestorDSCR"
        ? DSCRPostFields
        : getBridgePostFields(toorakProduct, loanStage);
    default:
      break;
  }
};

/* --------------------------------------- NEW CODE -------------------------------------------------------------- */
const fesDscrBorrowerCreditScore = {
  fieldName: "borrowerCreditScore",
  backendKey: "originalCreditScoreMedian",
  fieldLabel: "Borrower Credit Score (Median)",
  fieldId: "fes-CreditscoreActual",
  required: (borrowerInfo: any) => {
    if (
      borrowerInfo?.foreignNationalString &&
      borrowerInfo?.foreignNationalString !== "Yes"
    ) {
      return true;
    }
    return false;
  },
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "originalCreditScoreMedian",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const fesForeignNational = {
  fieldName: "foreignNationalString",
  fieldLabel: "Foreign National",
  backendKey: "foreignNationalString",
  required: () => false,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: yesNoDropdown.map((item) => ({ value: item, label: item })),
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "customer.foreignNationalString",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const fesDscrOriginalCreditScoreMedian = {
  fieldName: "borrowerCreditScore",
  backendKey: "originalCreditScoreMedian",
  fieldLabel: "Original Credit Score (Median)",
  required: (borrowerInfo: any) => {
    if (
      borrowerInfo?.foreignNationalString &&
      borrowerInfo?.foreignNationalString !== "Yes"
    ) {
      return true;
    }
    return false;
  },
  fieldType: (loanType: string) => "number",
  colSpan: 4,
  isVisible: (data: any) => data?.borrowerType !== "Entity",
  commentField: "originalCreditScoreMedian",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const entityIsPrimaryBorrower = {
  fieldName: "isPrimaryBorrower",
  fieldLabel: "Primary Borrower",
  backendKey: "isPrimary",
  required: () => true,
  fieldType: () => "radioGroup",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "borrowerType",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const entityBorrowingEntityName = {
  fieldName: "borrowingEntityName",
  fieldLabel: "Borrower Entity Name",
  backendKey: "accountName",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) =>
    isRequired("borrowingEntityName", borrowerInfo, borrowersData, loanStage),
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "customer.accountName",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const individualDSCRPostBorrowerEmail = {
  fieldName: "borrowerEmail",
  fieldLabel: "Borrower Email",
  backendKey: "email",
  required: () => true,
  fieldType: (loanType: string) => "string",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: `customer.contactList[0].email`,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrFESForeignNationString = {
  fieldName: "foreignNationalString",
  fieldLabel: "Foreign National Flag(Borrower or Guarantor)",
  backendKey: "foreignNationalString",
  required: () => true,
  fieldType: (loanType: string) => "dropDown",
  dropDownData: foreignNationalOptions.map((item) => ({
    value: item,
    label: item
  })),
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: "customer.foreignNationalString",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const individualDSCRPostBorrowerAddress = {
  fieldName: "billingAddress",
  fieldLabel: "Borrower Full Billing Address",
  backendKey: "addressLine1",
  required: (borrowerInfo: any, borrowersData: any, loanStage: any) => false,
  fieldType: (loanType: string) => "autocomplete",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: `customer.addressList[0].addressLine1`,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrPostIndividualBillingPhoneNumber = {
  fieldName: "billingPhoneNumber",
  fieldLabel: "Borrower Billing Phone Number",
  backendKey: "contactNumber",
  required: () => false,
  fieldType: (loanType: string) => "phone",
  colSpan: 4,
  isVisible: (data: any) => true,
  commentField: `customer.contactList[0].contactNumber`,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const bridgeEntityType = {
  fieldName: "entityType",
  fieldLabel: "Entity Type",
  backendKey: "entityType",
  required: (data: any) =>  data?.borrowerType === "Entity",
  fieldType: (loanType: string) => "dropDown",
  dropDownData: bridgeEntityTypeDropDownValues,
  colSpan: 4,
  isVisible: (data: any) =>  data?.borrowerType === "Entity",
  commentField: "customer.entityType",
  errorMsg: "Please select value",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};
const dscrEntityType = {
  fieldName: "entityType",
  fieldLabel: "Entity Type",
  backendKey: "entityType",
  required: (data: any) =>  data?.borrowerType === "Entity",
  fieldType: (loanType: string) => "dropDown",
  dropDownData: entityTypeDropDownValues,
  colSpan: 4,
  isVisible: (data: any) =>  data?.borrowerType === "Entity",
  commentField: "customer.entityType",
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

export const getCreditEventFields = () => [
  lastFcDate,
  lastSsDate,
  lastDilDate,
  lastBkDate,
  lastFcMonths,
  lastSsMonths,
  lastDilMonths,
  lastBkMonths,
  lastDelinquencyDate,
  lastDelinquencyMonths
];

const getBridgeFES14FamilyFields = () => [
  borrowerExperience,
  experienceIn3To10Years,
  fesForeignNational,
  fesDscrOriginalCreditScoreMedian,
  inState,
  bridgeEntityType
];

const getBridgeFESMultifamilyFields = () => [
  borrowerExperience,
  experienceIn3To10Years,
  fesForeignNational,
  fesDscrOriginalCreditScoreMedian,
  bridgeEntityType
];

const getBridgeFESGroundUpFields = () => [
  borrowerGUCExperience,
  heavyRehabExperience,
  fesForeignNational,
  fesDscrBorrowerCreditScore,
  bridgeEntityType
];

const getDSCRFES14FamilyFields = () => [
  borrowerCreditScore,
  dscrFESForeignNationString,
  borrowerType,
  dscrFirstTimeHomeBuyer,
  dscrEntityType
];

const getDSCRFESMultifamilyFields = () => [
  borrowerCreditScore,
  dscrFESForeignNationString,
  borrowerType,
  dscrFirstTimeHomeBuyer,
  dscrEntityType
];

const getDSCRFESGroundUpFields = () => [
  borrowerCreditScore,
  dscrFESForeignNationString,
  borrowerType,
  dscrFirstTimeHomeBuyer,
  dscrEntityType
];

const getBridgePreIndividualDefaultFields = () => [
  borrowerType,
  isPrimaryBorrower,
  firstName,
  lastName,
  foreignNational,
  originalCreditScoreMedian,
  originalCreditReportDate,
  { ...borrowerEmail },
  billingAddress,
  { ...billingPhoneNumber, required: () => false },
  borrowerExperience,
  experienceIn3To10Years,
  creditEvent
];

const getBridgePreIndividualGroundUpFields = () => [
  borrowerType,
  isPrimaryBorrower,
  firstName,
  lastName,
  foreignNational,
  originalCreditScoreMedian,
  originalCreditReportDate,
  { ...borrowerEmail },
  billingAddress,
  billingPhoneNumber,
  borrowerGUCExperience,
  heavyRehabExperience,
  creditEvent
];

const getDSCRPostEntityDefaultFields = () => [
  borrowerType,
  // entityIsPrimaryBorrower,
  entityBorrowingEntityName,
  { ...borrowerEmail,  required: (data: any) => true },
  { ...billingAddress,  required: (data: any) => false,},
  billingPhoneNumber,
  dscrEntityType
];

const getBridgePostEntityDefaultFields = () => [
  borrowerType,
  // entityIsPrimaryBorrower,
  entityBorrowingEntityName,
  borrowerEmail,
  billingAddress,
  billingPhoneNumber,
  borrowerExperience,
  experienceIn3To10Years,
  bridgeEntityType
];

const getBridgePostEntityGroundUpFields = () => [
  borrowerType,
  // entityIsPrimaryBorrower,
  entityBorrowingEntityName,
  borrowerEmail,
  billingAddress,
  billingPhoneNumber,
  borrowerGUCExperience,
  heavyRehabExperience,
  bridgeEntityType
];

const getDSCRPreEntityDefaultFields = () => [
  borrowerType,
  // entityIsPrimaryBorrower,
  entityBorrowingEntityName,
  borrowerEmail,
  billingAddress,
  billingPhoneNumber,
  dscrEntityType
];
const getBridgePreEntityGroundUpFields = () => [
  borrowerType,
  // entityIsPrimaryBorrower,
  entityBorrowingEntityName,
  borrowerEmail,
  { ...billingAddress, required: (data: any) => false },
  billingPhoneNumber,
  borrowerGUCExperience,
  heavyRehabExperience,
  bridgeEntityType
];
const getBridgePreEntityDefaultFields = () => [
  borrowerType,
  // entityIsPrimaryBorrower,
  entityBorrowingEntityName,
  borrowerEmail,
  { ...billingAddress, required: (data: any) => false },
  billingPhoneNumber,
  borrowerExperience,
  experienceIn3To10Years,
  bridgeEntityType
];

const getPostDSCREntityBorrowerFields = (toorakProductType: string) => {
  switch (toorakProductType) {
    // case LoanSizerEnum.groundUp:
    //   return getDSCRPreEntityGroundUpFields();
    default:
      return getDSCRPostEntityDefaultFields();
  }
};
const getPostBridgeEntityBorrowerFields = (toorakProductType: string) => {
  switch (toorakProductType) {
    case LoanSizerEnum.groundUp:
      return getBridgePostEntityGroundUpFields();
    default:
      return getBridgePostEntityDefaultFields();
  }
};

const getPreDSCREntityBorrowerFields = (toorakProductType: string) => {
  switch (toorakProductType) {
    // case LoanSizerEnum.groundUp:
    //   return getDSCRPreEntityGroundUpFields();
    default:
      return getDSCRPreEntityDefaultFields();
  }
};
const getPreBridgeEntityBorrowerFields = (toorakProductType: string) => {
  switch (toorakProductType) {
    case LoanSizerEnum.groundUp:
      return getBridgePreEntityGroundUpFields();
    default:
      return getBridgePreEntityDefaultFields();
  }
};

const getFESBridgeBorrowerFields = (toorakProductType: string) => {
  switch (toorakProductType) {
    case LoanSizerEnum.oneFourFamily:
      return getBridgeFES14FamilyFields();
    case LoanSizerEnum.multifamilyMixedUse:
      return getBridgeFESMultifamilyFields();
    case LoanSizerEnum.groundUp:
      return getBridgeFESGroundUpFields();
    default:
      return [];
  }
};

const getFESDSCRBorrowerFields = (toorakProductType: string) => {
  switch (toorakProductType) {
    case LoanSizerEnum.oneFourFamily:
      return getDSCRFES14FamilyFields();
    case LoanSizerEnum.multifamilyMixedUse:
      return getDSCRFESMultifamilyFields();
    case LoanSizerEnum.groundUp:
      return getDSCRFESGroundUpFields();
    default:
      return [];
  }
};

const getPreEntityBorrowerFields = (
  toorakProductType: string,
  loanType: string
) => {
  switch (loanType) {
    case "InvestorDSCR":
      return getPreDSCREntityBorrowerFields(toorakProductType);
    case "BridgeLoan":
      return getPreBridgeEntityBorrowerFields(toorakProductType);
    default:
      return [];
  }
};

const getPostEntityBorrowerFields = (
  toorakProductType: string,
  loanType: string
) => {
  switch (loanType) {
    case "InvestorDSCR":
      return getPostDSCREntityBorrowerFields(toorakProductType);
    case "BridgeLoan":
      return getPostBridgeEntityBorrowerFields(toorakProductType);
    default:
      return [];
  }
};

const getInvestorDSCRPostIndividualGroundUpFields = () => [
  borrowerType,
  dscrBorrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  dscrForeignNationString,
  originalCreditScoreMedian,
  originalCreditReportDate,
  individualDSCRPostBorrowerEmail,
  individualDSCRPostBorrowerAddress,
  dscrPostIndividualBillingPhoneNumber,
  dscrFirstTimeHomeBuyer,
  creditEvent
  // borrowerExperience
];

const getInvestorDSCRPreIndividualGroundUpFields = () => [
  borrowerType,
  dscrBorrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  dscrForeignNationString,
  originalCreditScoreMedian,
  originalCreditReportDate,
  borrowerEmail,
  billingAddress,
  { ...billingPhoneNumber, required: () => false },
  dscrFirstTimeHomeBuyer,
  creditEvent
  // borrowerExperience
];

const getInvestorDSCRPreIndividualDefaultFields = () => [
  borrowerType,
  dscrBorrowingEntityName,
  isPrimaryBorrower,
  firstName,
  lastName,
  dscrForeignNationString,
  originalCreditScoreMedian,
  originalCreditReportDate,
  borrowerEmail,
  billingAddress,
  { ...billingPhoneNumber, required: () => false },
  dscrFirstTimeHomeBuyer,
  creditEvent
  // borrowerExperience
];

const getPreInvestorDSCRIndividualFields = (
  toorakProductType: string,
  loanType: string
) => {
  switch (toorakProductType) {
    case LoanSizerEnum.groundUp:
      return getInvestorDSCRPreIndividualGroundUpFields();
    default:
      return getInvestorDSCRPreIndividualDefaultFields();
  }
};

const getPreBridgeIndividualBorrowerFields = (
  toorakProductType: string,
  loanType: string
) => {
  switch (toorakProductType) {
    // case LoanSizerEnum.oneFourFamily:
    //   return getBridgePreIndividual14FamilyFields();
    // case LoanSizerEnum.multifamilyMixedUse:
    //   return getBridgePreIndividualMultifamiliFields();
    case LoanSizerEnum.groundUp:
      return getBridgePreIndividualGroundUpFields();
    default:
      return getBridgePreIndividualDefaultFields();
  }
};

const getPreIndividualBorrowerFields = (
  toorakProductType: string,
  loanType: string
) => {
  switch (loanType) {
    case "InvestorDSCR":
      return getPreInvestorDSCRIndividualFields(toorakProductType, loanType);
    case "BridgeLoan":
      return getPreBridgeIndividualBorrowerFields(toorakProductType, loanType);
    default:
      return [];
  }
};

const getPostIndividualBorrowerFields = (
  toorakProductType: string,
  loanType: string
) => {
  switch (loanType) {
    case "InvestorDSCR":
      return getInvestorDSCRPostIndividualGroundUpFields();
    case "BridgeLoan":
      return getPreBridgeIndividualBorrowerFields(toorakProductType, loanType);
    default:
      return [];
  }
};

const getFESBorrowerFields = (toorakProductType: string, loanType: string) => {
  switch (loanType) {
    case "InvestorDSCR":
      return getFESDSCRBorrowerFields(toorakProductType);
    case "BridgeLoan":
      return getFESBridgeBorrowerFields(toorakProductType);
    default:
      return [];
  }
};

export const getEntityBorrowerFields = (
  toorakProductType: string,
  loanStage: string,
  loanType: string
) => {
  switch (loanStage) {
    case "FES":
      return getFESBorrowerFields(toorakProductType, loanType);
    case "PRE":
      return getPreEntityBorrowerFields(toorakProductType, loanType);
    case "POST":
      return getPostEntityBorrowerFields(toorakProductType, loanType);
    default:
      break;
  }
};

export const getIndividualBorrowerFields = (
  toorakProductType: string,
  loanStage: string,
  loanType: string
) => {
  switch (loanStage) {
    case "FES":
      return getFESBorrowerFields(toorakProductType, loanType);
    case "PRE":
      return getPreIndividualBorrowerFields(toorakProductType, loanType);
    case "POST":
      return getPostIndividualBorrowerFields(toorakProductType, loanType);
    default:
      break;
  }
};
